import { fetchWebPushRequest } from 'modules/webPush/actions';
import { selectWebPushIntegration } from 'modules/webPush/selectors';
import { WebPushIntegration } from 'modules/webPush/types';
import {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLegacyWebPushIntegration } from '../utils';

export type WebPushMetadataContextState = {
  /** Details of the existing web push integration. Only 1 integration can exist per project. */
  integration: WebPushIntegration | undefined;
  /** Whether existing web push integration is legacy Firebase type (as opposed to Firebase JSON). */
  isLegacyIntegration: boolean;
};

const WebPushMetadataContext = createContext<WebPushMetadataContextState>({
  integration: undefined,
  isLegacyIntegration: false,
});

export const WebPushMetadataProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const webPushIntegration = useSelector(selectWebPushIntegration);

  const [integration, setIntegration] = useState<WebPushIntegration>();

  const isLegacyIntegration = useMemo(
    () => getIsLegacyWebPushIntegration(integration),
    [integration],
  );

  useEffect(() => {
    dispatch(fetchWebPushRequest());
  }, []);

  useEffect(() => {
    setIntegration(webPushIntegration);
  }, [webPushIntegration]);

  const value = {
    integration,
    isLegacyIntegration,
  };

  return (
    <WebPushMetadataContext.Provider value={value}>{children}</WebPushMetadataContext.Provider>
  );
};

export const useWebPushMetadataContext = () => useContext(WebPushMetadataContext);
